import React, { FC, useEffect } from "react";

import { Form, Field } from "react-final-form";
import { Checkbox, TextField } from "final-form-material-ui";

import { withStyles, createStyles, WithStyles, Theme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Check from "@material-ui/icons/Check";
import Close from "@material-ui/icons/Close";

import { vessel, getVesselByCode } from "../../lib/api/vessel";

import { SnackContext } from "../../lib/context/SnackContext";
import { GenerateErrorMessage } from "../../lib/helpers/string_methods";
import { validateUniqueField } from "../../lib/helpers/string_methods";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(1),
      width: "100%",
    },
    formField: {
      display: "grid",
      gridTemplateColumns: "120px 250px",
      gap: `${theme.spacing(2)}px`,
      "& label": {
        textAlign: "right",
        alignSelf: "center",
      },
    },
    formBody: {
      display: "flex",
      flexDirection: "column",
      gap: `${theme.spacing(1)}px`,
    },
    formSelect: {
      textAlign: "left",
      width: "300px",
      height: "50px",
      padding: 0,
      marginTop: "-14px",
    },
    actionButtons: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-end",
      gap: `${theme.spacing(1)}px`,
      marginTop: `${theme.spacing(2)}px`,
    },
  });

type VesselDetailProps = {
  vessel_id: number | string;
  onClose: any;
  onSubmittedEvent: any;
} & WithStyles<typeof styles>;

const formatField = (value: string, removewhitespace: boolean = false) => (removewhitespace ? value.replaceAll(" ", "").toUpperCase() : value.toUpperCase());

const VesselDetailUnstyled: React.SFC<VesselDetailProps> = (props) => {
  const { classes, vessel_id, onClose, onSubmittedEvent } = props;
  const { updateSnack } = React.useContext(SnackContext);

  const [vesselData, setVesselData] = React.useState({});

  useEffect(() => {
    loadData();
  }, [vessel_id]);

  const validateVesselCode = async (values) => {
    return await validateUniqueField(values, {
      field: "code",
      model: vessel,
      transform: (v) => v ? v.trim().toUpperCase() : "",
    });
  };

  const loadData = async () => {
    const newData = {
      id: undefined,
      vesselType: undefined,
      code: undefined,
      description: undefined,
    };

    if (!vessel_id) {
      setVesselData(newData);
    } else {
      const result = await vessel.single(vessel_id);
      setVesselData(result);
    }
  };

  const onSubmitted = () => {
    onSubmittedEvent();
  };

  const onSubmit = async (vesselData, reactFinalForm) => {
    let id = vesselData["id"];
    delete vesselData["id"];

    try {
      const { code, description } = vesselData;
      const formData = { data: { ...vesselData, code: formatField(code, true), description: formatField(description) } };

      if (!id) {
        await vessel.create(formData);
      } else {
        await vessel.update(id, formData);
      }
    } catch (error) {
      const err = GenerateErrorMessage(error, "Error creating/updating Vessel");
      updateSnack({ show: true, color: "red", message: err });
    }
    onSubmitted();
    loadData();
  };

  return (
    <div style={{ marginTop: "-4px" }} className={classes.root}>
      <Form
        initialValues={{ ...vesselData }}
        onSubmit={onSubmit}
        validate={validateVesselCode}
        render={({ handleSubmit, form }) => (
          <form onSubmit={handleSubmit}>
            <form onSubmit={handleSubmit}>
              <div className={classes.formBody}>
                <TableFieldCheckbox classes={classes} field="active" title="Active" />
                <TableFieldText classes={classes} field="vesselType" title="Vessel Type" disabled={false} />
                <TableFieldText classes={classes} field="code" title="Code" disabled={false} />
                <TableFieldText classes={classes} field="description" title="Description" disabled={false} />
              </div>
              <div className={classes.actionButtons}>
                <Button style={{ marginRight: "10px" }} color="secondary" variant="outlined" onClick={onClose}>
                  <Close />
                </Button>
                <Button disabled={!form.getState().dirty} style={{ marginRight: "16px" }} type="submit" color="primary" variant="outlined">
                  <Check />
                </Button>
              </div>
            </form>
          </form>
        )}
      />
    </div>
  );
};
export default withStyles(styles)(VesselDetailUnstyled);

type FormCheckboxProps = {
  field: string;
  title: string;
} & WithStyles<typeof styles>;

const TableFieldCheckbox: FC<FormCheckboxProps> = ({ classes, field, title }) => {
  return (
    <div className={classes.formField}>
      <label>{title}:</label>
      <div>
        <Field fullWidth name={field} component={Checkbox} type="checkbox" />
      </div>
    </div>
  );
};

const TableFieldText: FC<{ field: string; title: string; disabled: boolean } & WithStyles<typeof styles>> = (props) => {
  const { classes, field, title, disabled } = props;
  return (
    <div className={classes.formField}>
      <label>{title}:</label>
      <div>
        <Field fullWidth name={field} component={TextField} type="text" disabled={disabled} />
      </div>
    </div>
  );
};
